import { useDebouncedValue } from '@mantine/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OsLotReviewListItem } from '../../lib/types/os-lot-reviews';

import { useColFilter, useFiltersStore } from '@data-access';

export type OsLotReviewsQuickFilter = 'yield_fail' | 'tonnage_fail' | 'area_fail';

const QUICK_FILTER_IDS: Record<OsLotReviewsQuickFilter, string> = {
  yield_fail: 'yield_ok',
  tonnage_fail: 'tonnage_ok',
  area_fail: 'area_ok',
};

export function useOsLotReviewsFilterOptions(results: OsLotReviewListItem[]) {
  return useMemo(() => {
    const yearSet = new Set<number>();
    const speciesMap = new Map<number, string>();

    results.forEach((lot) => {
      yearSet.add(lot.year);
      if (lot.species) {
        speciesMap.set(lot.species.id, lot.species.name);
      }
    });

    const defaultYears = [2026, 2025, 2024, 2023, 2022];
    defaultYears.forEach((y) => yearSet.add(y));

    const years = Array.from(yearSet).sort((a, b) => b - a);
    const species = Array.from(speciesMap.entries())
      .map(([id, name]) => ({ value: id.toString(), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return { years, species };
  }, [results]);
}

export function useOsLotReviewsFilters() {
  const table = 'osLotReviews' as const;
  const filters = useFiltersStore.use[table]().filters;
  const setFilter = useFiltersStore((s) => s.setFilter);
  const resetFilter = useFiltersStore((s) => s.resetFilter);
  const setPagination = useFiltersStore((s) => s.setPagination);

  const farmNameFilter = filters.find((f) => f.id === 'farm_name')?.value as
    | string
    | undefined;
  const [searchInput, setSearchInput] = useState(farmNameFilter ?? '');
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);
  const skipInitialSearchSync = useRef(true);

  const [year, setYear] = useColFilter<string | null>(table, 'year');
  const [speciesId, setSpeciesId] = useColFilter<string | null>(
    table,
    'species__id',
  );
  const allOkFilter = filters.find((f) => f.id === 'all_ok');
  const [reviewStatus, setReviewStatus] = useColFilter<string | null>(
    table,
    'review_status',
  );
  const hasDataFilter = filters.find((f) => f.id === 'has_data');
  const hasData = hasDataFilter?.value === true;

  const resetPage = useCallback(() => {
    setPagination(table, { pageIndex: 0, pageSize: 25 });
  }, [setPagination]);

  useEffect(() => {
    if (skipInitialSearchSync.current) {
      skipInitialSearchSync.current = false;
      if (!debouncedSearch.trim() && !farmNameFilter) {
        return;
      }
    }

    const next = debouncedSearch.trim();
    if (next) {
      setFilter(table, 'farm_name', next);
    } else {
      resetFilter(table, 'farm_name');
    }
    resetPage();
  }, [debouncedSearch, farmNameFilter, resetFilter, resetPage, setFilter]);

  const quickFilters = useMemo(() => {
    const active = new Set<OsLotReviewsQuickFilter>();
    if (filters.some((f) => f.id === 'yield_ok' && f.value === false)) {
      active.add('yield_fail');
    }
    if (filters.some((f) => f.id === 'tonnage_ok' && f.value === false)) {
      active.add('tonnage_fail');
    }
    if (filters.some((f) => f.id === 'area_ok' && f.value === false)) {
      active.add('area_fail');
    }
    return active;
  }, [filters]);

  const toggleQuickFilter = useCallback(
    (key: OsLotReviewsQuickFilter) => {
      const filterId = QUICK_FILTER_IDS[key];
      const isActive = quickFilters.has(key);
      if (isActive) {
        resetFilter(table, filterId);
      } else {
        setFilter(table, filterId, false);
      }
      resetPage();
    },
    [quickFilters, resetFilter, resetPage, setFilter],
  );

  const handleYearChange = useCallback(
    (value: string | null) => {
      if (value) {
        setYear(value);
      } else {
        resetFilter(table, 'year');
      }
      resetPage();
    },
    [resetFilter, resetPage, setYear],
  );

  const handleSpeciesChange = useCallback(
    (value: string | null) => {
      if (value) {
        setSpeciesId(value);
      } else {
        resetFilter(table, 'species__id');
      }
      resetPage();
    },
    [resetFilter, resetPage, setSpeciesId],
  );

  const handleQueueChange = useCallback(
    (value: string) => {
      if (value === 'all') {
        resetFilter(table, 'all_ok');
      } else {
        const next = value === 'ok';
        setFilter(table, 'all_ok', next);
      }
      resetFilter(table, 'yield_ok');
      resetFilter(table, 'tonnage_ok');
      resetFilter(table, 'area_ok');
      resetPage();
    },
    [resetFilter, resetPage, setFilter],
  );

  const handleReviewStatusChange = useCallback(
    (value: string | null) => {
      if (value) {
        setReviewStatus(value);
        setFilter(table, 'review_status', value);
      } else {
        resetFilter(table, 'review_status');
        setReviewStatus(null);
      }
      resetPage();
    },
    [resetFilter, resetPage, setFilter, setReviewStatus],
  );

  const handleHasDataChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        setFilter(table, 'has_data', true);
      } else {
        resetFilter(table, 'has_data');
      }
      resetPage();
    },
    [resetFilter, resetPage, setFilter],
  );

  const queueValue = allOkFilter?.value === true 
    ? 'ok' 
    : allOkFilter?.value === false 
    ? 'issues' 
    : 'all';

  return {
    filters,
    searchInput: searchInput ?? '',
    setSearchInput,
    year: year?.toString() ?? null,
    speciesId,
    queueValue,
    reviewStatus,
    hasData,
    quickFilters,
    toggleQuickFilter,
    handleYearChange,
    handleSpeciesChange,
    handleQueueChange,
    handleReviewStatusChange,
    handleHasDataChange,
  };
}
